import React from 'react';
import { REQUEST_TYPES, REQUEST_TYPES_DB } from '../../BudgetRequests/budgetRequestsHelpers.js';
import { CREATE, DELETE, EDIT, PRIORITY, PROGRAM, PROGRAM_DEPARTMENT, PROGRAM_DIVISION, REQUEST, USER_GROUP } from '../../../static/keyWords.js';
import { REQUIRED_FIELD_TEXT } from '../../../static/constants.js';
import { APP_ICONS } from '../../../static/appIcons.js';

const ALIAS= 'alias';
const PLURAL_FORM_ALIAS= 'pluralFormAlias';
const versionKey = 'nameConventions_version';
let cachedMessages = null;
let cachedVersion = null;

// Listen for cross-tab changes to invalidate cache
window.addEventListener('storage', (event) => {
  if (event.key === versionKey) {
    cachedMessages = null;
  }
});

function getVersion() {
  return localStorage.getItem(versionKey) || '0';
}

function interpolate(prop, ALIAS, nameConventions) {
  return nameConventions?.[prop]?.[ALIAS] || '';
}

export const messageManager = (nameConventions) => {

  const currentVersion = getVersion();

  if (cachedMessages && cachedVersion === currentVersion) {
    return cachedMessages;
  }

  cachedVersion = currentVersion; 

  const messages = {
    PBB_STRATEGIST: {
      TITLE: APP_ICONS.PBB_STRATEGIST,
      SAVINGS_MODAL_TITLE: 'Savings goal wizard',
      PROGRAM_DOSSIER_MODAL_TITLE: `${interpolate(PROGRAM, ALIAS, nameConventions)} dossier`,
      QUICK_WINS_MODAL_TITLE: 'Find quick wins',
      IMAGINE_WITH_AI_MODAL_TITLE: 'Imagine with AI',
      EMPTY_STATE_MESSAGE: `Welcome to ${APP_ICONS.PBB_STRATEGIST}! Start a conversation by asking a question or describing what you'd like help with.`,
      INLINE_INFO_MESSAGE: 'AI assistant is in use. Buttons will be enabled once the whole response has been sent.',
      THINKING_RESPONSE: 'AI assistant is in use',
      KIND_OF_OPPORTUNITY: 'What kind of opportunity?',
      SAVINGS_TARGET: 'Minimum savings target',
      FAILED_TO_COPY_CLIPBOARD: 'Failed to copy to clipboard',
      FIELD_NAME: {
        PROGRAM_NAME: 'programName',
        TARGET_SAVINGS_AMOUNT: 'targetSavingsAmount',
        DEPARTMENT_NAME: 'departmentName',
        BOX_CHOICE: 'box-choice'
      },
      LABELS: {
        PROGRAM_NAME: `${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} name`,
        CHECKBOX_LABEL: 'Include visual infographic',
        TARGET_SAVINGS_AMOUNT: 'Target savings amount',
        EXPLORE_IDEA: 'Explore your own idea',
      },
      PARAGRAPHS: {
        PROGRAM_DOSSIER_MAIN_PARAGRAPH: 'Get comprehensive information about a specific program including cost, revenue, PBB guidance, benchmarks, and savings opportunities.',
        PROGRAM_DOSSIER_SECONDARY_PARAGRAPH: 'The program name should match exactly as it appears in your uploaded documents.',
        SAVINGS_PARAGRAPH: 'Fast-track your savings analysis with guided questions',
        QUICK_WINS_PARAGRAPH: 'Define what ‘quick wins’ your organization need and AI will help you find it!',
        IMAGINE_WITH_AI_TOP: `Pick a starter question below — the ${APP_ICONS.PBB_STRATEGIST} will pull real examples from government, cross-sector analogs, and creative possibilities grounded in your data. Or use "Explore your own idea" at the bottom to push into uncharted territory.`,
        IMAGINE_WITH_AI_BOTTOM: 'Have a specific AI idea, service, or question in mind? Describe it below and the Strategist will run a creative exploration — real examples where they exist, thoughtful speculation where they don\'t. The department scope above will apply here too if filled in.',
      },
      FIELD_TYPE: 'text',
      NUMBER_FILED_TYPE: 'number',
      LABEL_SLOT: 'label',
      MODALS: {
        PROGRAM_DOSSIER_MODAL: 'dossier',
        SAVINGS_MODAL: 'savings'
      },
      CONVERSATION: {
        RENAME_SUCCESS: 'Conversation renamed successfully.',
        RENAME_ERROR: 'There was a problem renaming the conversation. Please try again.',
        DELETE_SUCCESS: 'Conversation deleted successfully.',
        DELETE_ERROR: 'There was a problem deleting the conversation. Please try again.',
        HANDLE_FEEDBACK_ERROR: 'There was a problem submitting your feedback. Please try again.',
        GET_MESSAGES_ERROR: 'There was a problem fetching the conversation messages. Please try again.',
      }
    },
    COST_BENCHMARK: {
      TITLE: 'Cost benchmark',
      GRAPHIC_PAGE_CARD_TITLE: 'Benchmark results',
      BOUNDARIES_MODAL_TITLE: 'Boundaries',
      EMPTY_STATE: {
        NO_PROGRAMS: `No ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} found`,
        TITLE: 'Boundaries do not match. Adjust to get better results',
        BUTTON: `Add ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
        NO_SIMILAR_PROGRAMS: `No similar ${interpolate(PROGRAM, ALIAS, nameConventions)}. Adjust boundaries.`,
        MISSING_POPULATION: 'No population added. Contact admin user to adjust',
        NO_DATA_TO_DISPLAY: 'No data to display.',
      },
      DISABLED_BUTTON: {
        NO_DATA: 'No data to export',
        GENERATING_DOCUMENT: 'Document generation in progress',
      }
    },
    AI_ANALYTICS: {
      TITLE: 'AI analytics'
    },
    PROGRAM_INVENTORY: {
      MAIN_TITLE: APP_ICONS.PROGRAM_INVENTORY,
      UPLOAD_TITLE: `${interpolate(PROGRAM, ALIAS, nameConventions)} upload`,
      ADD_NEW_PROGRAM_DIALOG_TITLE: `Create new ${interpolate(PROGRAM, ALIAS, nameConventions)}`,
      ADD_NEW_PROGRAM_SUCCESS_MESSAGE: (numberOfPrograms) => `Created new ${interpolate(PROGRAM, numberOfPrograms > 1 ? PLURAL_FORM_ALIAS : ALIAS, nameConventions)} successfully.`,
      ADD_NEW_PROGRAM_ERROR_MESSAGE: `Failed to create new ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}. Please try again later.`,
      COMBINE_PROGRAMS_TITLE: `Combine ${interpolate(PROGRAM, ALIAS, nameConventions)}`,
      COMBINE_PROGRAMS_DIALOG_MESSAGE: `${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} to be combined:`,
      COMBINE_PROGRAMS_CONSOLIDATION_MESSAGE: `Please select main ${interpolate(PROGRAM, ALIAS, nameConventions)} and ${interpolate(USER_GROUP, ALIAS, nameConventions)} to keep after consolidation:`,
      COMBINE_PROGRAM_DESCRIPTION_MESSAGE: `You can edit the description of the new ${interpolate(PROGRAM, ALIAS, nameConventions)}:`,
      FROM_STARTER_KIT_TITLE: `Create new ${interpolate(PROGRAM, ALIAS, nameConventions)} from starter kit`,
      FROM_STARTER_KIT_MESSAGE_EXPLANATION: `${interpolate(PROGRAM, ALIAS, nameConventions)} starter kits are collections of ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} that Priority Based Budgeting
  provides that are common to most municipalities. Using them is as easy
  as selecting the ones you want and editing the ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} inside to fit your
  city!`,
      FROM_STARTER_KIT_MESSAGE_EXPLANATION_STEP_TWO: `These are the ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} that
    can be added to your inventory. You can select the 
    ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} you want to keep in
    your budget.`,
      FROM_STARTER_KIT_DEPARTMENT_AND_DIVISION_MESSAGE: `Please select
  ${interpolate(PROGRAM_DEPARTMENT, ALIAS, nameConventions)} and
  ${interpolate(PROGRAM_DIVISION, ALIAS, nameConventions)} for
  where your selected ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} must appear:`,
      ADD_NEW_PROGRAM_FROM_SEARCH_TITLE: `Create new ${interpolate(PROGRAM, ALIAS, nameConventions)} from search`,
      ADD_NEW_PROGRAM_FROM_SEARCH_DIALOG_MESSAGE: `Add ${interpolate(PROGRAM, ALIAS, nameConventions)} from what other cities are using. Search for a ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} or ${interpolate(PROGRAM, ALIAS, nameConventions)} description and see ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} that you can add to your ${interpolate(PROGRAM, ALIAS, nameConventions)} inventory.`,
      ADD_NEW_PROGRAM_FROM_SEARCH_SEARCH_FIELD_MESSAGE: `Ask to narrow down your search for ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}:`,
      ADD_NEW_PROGRAM_FROM_SEARCH_NOT_FOUND_MESSAGE: 'Sorry, we couldn\'t find what you\'re looking for.',
      ADD_NEW_PROGRAM_FROM_SEARCH_INFO_MESSAGE: `Search for a ${interpolate(PROGRAM, ALIAS, nameConventions)} or ${interpolate(PROGRAM, ALIAS, nameConventions)} description to get started.`,
      ADD_NEW_PROGRAM_FROM_SEARCH_RESULT_MESSAGE: `Please select ${interpolate(PROGRAM_DEPARTMENT, ALIAS, nameConventions)} and ${interpolate(PROGRAM_DIVISION, ALIAS, nameConventions)} for where your selected ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} must appear:`,
      UPLOAD_DIALOG_TITLE: `${interpolate(PROGRAM, ALIAS, nameConventions)} upload`,
      UPLOAD_DIALOG_MESSAGE: 'Please upload one Excel sheet.',
      DOWNLOAD_PROGRAM_INVENTORY: `Download ${interpolate(PROGRAM, ALIAS, nameConventions)} inventory`,
      DELETE_DIALOG_TITLE: `Delete ${interpolate(PROGRAM, ALIAS, nameConventions)}`,
      DELETE_DIALOG_TITLE_MULTIPLE: `Delete ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      DELETE_PROGRAM_DIALOG_CONTENT_MESSAGE: `Are you sure you want to permanently delete this ${interpolate(PROGRAM, ALIAS, nameConventions)} from the system?`,
      DELETE_PROGRAM_DIALOG_CONTENT_MESSAGE_MULTIPLE: `Are you sure you want to permanently delete these ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions).toLowerCase()} from the system?`,
      DELETE_DIALOG_CONFIRMATION_MESSAGE: `Please note that this action will delete all information about the ${interpolate(PROGRAM, ALIAS, nameConventions)} from this budget only and this cannot be undone.`,
      DELETE_DIALOG_CONFIRMATION_MESSAGE_MULTIPLE: `Please note that this action will delete all information about the ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions).toLowerCase()} from this budget only and this cannot be undone.`,
      SUCCESSFULLY_SAVED_MESSAGE: 'Your changes have been successfully saved.',
      PROBLEM_SAVING_THE_PROGRAMS: `There was a problem saving the ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}.`,
      OTHER_USER_GROUPS_MESSAGE: `Note: ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} from other ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)} won't be uploaded.`,
      DELETE_UNSUCCESSFUL_ERROR: `Problem with deleting ${interpolate(PROGRAM, ALIAS, nameConventions)}!`,
      DELETE_UNSUCCESSFUL_ERROR_MULTIPLE: `Problem with deleting ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}!`,
      COMBINE_PROGRAMS_SUCCESS_MESSAGE: `${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} have been successfully combined`,
      UPLOAD_SUCCESS: `${interpolate(PROGRAM, ALIAS, nameConventions)} uploaded successfully`,
      DELETE_SUCCESS: `Successfully deleted ${interpolate(PROGRAM, ALIAS, nameConventions)}!`,
      DELETE_SUCCESS_MULTIPLE: `Successfully deleted ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}!`,
      SUCCESS_CREATING_FROM_STARTER_KIT:(OPTS)=> `Successfully created ${OPTS[0]} ${OPTS[1] ? interpolate(PROGRAM, ALIAS, nameConventions) : interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} to your inventory.`,
      ADD_FROM_STARTER_KIT_FILL_ALL_FIELDS: 'Please fill out all required fields.',
      DUPLICATE_PROGRAMS_MESSAGE: (OPTS) => `The following ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} are duplicates: ${OPTS.join(', ')}. Please adjust.`,
      RECOMMEND_PROGRAMS_DIALOG_TITLE: `Recommended ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      RECOMMENDED_PROGRAMS_DIALOG_MESSAGE: `These are the ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} that can be added to your inventory. You can select ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} you want to keep in your budget.`,
      RECOMMENDED_PROGRAMS_DIALOG_EMPTY_STATE_MESSAGE: 'No items found',
      GENERATE_PROGRAMS_DIALOG_MESSAGE: 'The following information is needed for generating recommendations',
      COMBINE_PROGRAMS_ERROR: `Failed to combine ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}.`,
      DUPLICATE_PROGRAMS_DIALOG_TITLE: `Duplicated ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      DUPLICATE_MAIN_PROGRAM_MESSAGE: `The following ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} are duplicates and were not saved:`,
      PROGRAM_PROCESS_OVERLAP: {
        TITLE: 'Program process analyzer',
        STEP_TITLE: 'Program key processes',
        STEP_ONE_ARIA_LABEL: (OPTS) => `Program process for ${OPTS}`,
        INVALID_PROGRAMS_FOR_ANALYSIS: `Some of the selected ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} have missing or empty name or description fields.`,
      },
      PROBLEM_PROGRAM_INVENTORY_CONTENT: `An error occurred while loading the ${interpolate(PROGRAM, ALIAS, nameConventions)} inventory content. Please try again.`,
      UPLOAD_VALIDATOR_MISSING_PROGRAM: `${interpolate(PROGRAM, ALIAS, nameConventions)} name is required.`,
      UPLOAD_VALIDATOR_MISSING_PROGRAM_DESCRIPTION: `${interpolate(PROGRAM, ALIAS, nameConventions)} description is required.`,
      UPLOAD_VALIDATOR_MISSING_SERVICE_TYPE: 'Service type is required.',
      UPLOAD_VALIDATOR_MISSING_USER_GROUP: `${interpolate(USER_GROUP, ALIAS, nameConventions)} is required.`
    },
    REPORTS: {
      MAIN_TITLE: APP_ICONS.REPORTS,
      LABELS: {
        REVIEW_DIALOG_SELECT_LABEL: 'Select a review type'
      },
      UPLOAD_DIALOG_TITLE: 'File upload',
      REVIEW_DOCUMENT_DIALOG_TITLE: 'Document review and critique',
      REVIEW_DOCUMENT_DIALOG_PARAGRAPH: 'System is using Open AI for converting data. ',
      BUILD_INSIGHT_REPORT_DIALOG_TITLE: 'Building insight PDF file',
      BUILD_INSIGHT_REPORT_DIALOG_HEADING: 'Please select which insight sets to include:',
      BUILD_A_FILE_PICTURE_IF_NEEDED: 'Upload organization logo (optional)',
      BUILD_DIALOG_SELECTED_SETS_HEADING: 'Selected sets',
      BUILD_REQUESTS_REPORT_DIALOG_TITLE: `Generate Budget ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)} PDF report`,
      DELETE_REPORT_WARNING_DIALOG_TITLE: 'Delete report',
      DELETE_REPORT_WARNING_DIALOG_CONTENT: 'This action will delete the selected report.',
      DELETE_REPORT_WARNING_DIALOG_CONFIRMATION: 'Are you sure you want to delete this report?',
      UPLOADED_REPORTS_FETCH_ERROR: 'An error occurred while loading the files. Something went wrong. Please try again.',
      REPORTS_DELETE_ERROR: 'An error occurred while deleting the file. Something went wrong. Please try again.',
      SAME_NAME_FILE_UPLOAD_ERROR: 'You already have a file with that name. Please change the name of the file.',
      REPORT_UPLOAD_ERROR: 'An error occurred while uploading the file. Something went wrong. Please try again.',
      UPLOADED_FILE_INFO_MISSING_LINK: 'Download link missing.Please try again.',
      UPLOAD_SUCCESS_MESSAGE: 'File uploaded successfully',
      DELETE_SUCCESS_MESSAGE: 'File deleted successfully.',
      NO_REPORTS_FOUND: 'No reports found.',
    },
    ORGANIZATION_EDITOR: {
      DELETE_DIALOG_TITLE: 'Delete organization',
      DELETE_DIALOG_CONTENT: 'The following organization will be deleted and everything associated with it',
      DELETE_DIALOG_CONFIRMATION: (OPTS)=>getDeleteDialogConfirmation(OPTS),
      TOAST_DELETE_SUCCESS: 'Organization has been successfully deleted.',
      TOAST_DELETE_ERROR_ACTIVE_ORGANIZATION: 'Can not delete currently active organization.',
      TOAST_CREATED_ORGANIZATION_SUCCESS: 'Organization has been successfully created.',
      TOAST_EDITED_ORGANIZATION_SUCCESS: 'Organization has been successfully edited.',
      TOAST_EDITING_ACTIVE_ORGANIZATION_ERROR: 'Cannot edit currently active organization.',
      MAIN_TITLE: APP_ICONS.ORGANIZATION_EDITOR,
      EDIT_DIALOG_TITLE: 'Edit organization',
      CREATE_DIALOG_TITLE: 'Create organization',
      COPY_ORGANIZATION_SUCCESS: 'Your changes have been successfully saved.',
      COPY_ORGANIZATION_ERROR: 'An error occurred while copying the organization. Please try again.',
      COPY_ORGANIZATION: 'Copy organization',
      NEW_ORGANIZATION_NAME: 'New organization name',
      ORGANIZATION_NAME_REQUIREMENT_ERROR: 'Organization name can be between 3 and 50 characters and contain uppercase and lowercase letters, numbers or the symbols , , _, -, : and whitespaces',
      DUPLICATE_NAME: 'This organization name already exists!',
      SELECT_USERS_FOR_NEW_ORGANIZATION: 'Choose which current users can have access to this new organization',
      EMPTY_ORGANIZATION_NAME_ERROR: REQUIRED_FIELD_TEXT,
      SPACES_ERROR: 'Organization name cannot contain leading or trailing spaces.',
      WORKSPACE_KEY_MESSAGE: 'Must match the value in Ops Center',
      ORGANIZATION_KEY_MESSAGE: 'Must match the value in Ops Center',
      POPULATION_MESSAGE: 'Population must be greater than or equal to 0',
      SAVING_ORGANIZATION_ERROR: 'An error occurred while saving the organization. Please try again.',
    },
    CONFIGURE_REQUESTS: {
      MAIN_TITLE: APP_ICONS.CONFIGURE_REQUESTS,
      IMAGE_MESSAGE: `Click on an item in the menu to configure ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)}`,
      NO_ACTIVE_BUDGET_SELECTED: 'No active budget selected. Please select a budget and try again.',
    },
    CONFIGURE_RESULTS: {
      EMPTY_STATE_TITLE: `No ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} yet.`,
      EMPTY_STATE_BUTTON_TEXT: `Add ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}`,
      EMPTY_STATE_MESSAGE: `Click on an item in the menu to configure ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}`
    },
    BOX_CHOICES: {
      TITLE: 'Box choices',
      DUPLICATE_TITLE: 'Duplicated box choices',
      DUPLICATE_ERROR: 'You have duplicated box choices. Please ensure all box choices are unique. This action can not be performed.',
      DUPLICATE_NAME_TITLE: 'Duplicated box type name',
      DUPLICATE_NAME_ERROR: 'You have duplicated box type name. Please ensure all box type names are unique. This action can not be performed.',
      DUPLICATE_TEXT_FIELD_ERROR: 'Box choice already exists',
      DELETE_DIALOG_TITLE: 'Delete box choice',
      DELETE_DIALOG_CONTENT: `Are you sure you want to permanently delete this box choice from the ${interpolate(REQUEST, ALIAS, nameConventions)} process?`,
      DELETE_DIALOG_CONFIRMATION: 'Please note that this action will delete all information about the box choice from all budgets and this cannot be undone.',
      CHANGE_TYPE_WARNING: 'Changing the type from Multiple choice to Single choice may result in the deletion of existing answers',
      EDIT_BOX_CHOICE: 'Edit box choice',
      ADD_BOX_CHOICE: 'Add box choice',
      ADD_SUCCESS: 'Box choice saved successfully.',
      EDIT_SUCCESS: 'Box choice updated successfully.',
      SAVE_OR_UPDATE_ERROR: 'There was a problem saving or updating your box type. Please refresh and try again.',
      FETCH_ERROR: 'There was a problem fetching the box types. Please refresh and try again.',
      DATABASE_CONFLICT_ERROR: 'There was a conflict. Please update the box choices one by one.',
      DELETE_ERROR: 'There was a problem deleting your box type. Please refresh and try again.',
      DELETE_SUCCESS: 'Box choice deleted successfully.',
      ADD_EDIT_FIELDS_LABELS: {
        NAME: 'Box choice type name',
        BOX_CHOICE_OPTION_LABEL: (OPTS) => `Box choice ${OPTS}`,
      }
    },
    QUESTIONS_MESSAGES: {
      TITLE: 'Questions',
      DUPLICATE_TITLE: 'Duplicated questions',
      DUPLICATE_ERROR: 'You have duplicated questions. Please ensure all questions are unique. This action can not be performed.',
      DUPLICATE_TEXT_FIELD_ERROR: 'Question already exists',
      DUPLICATE_IN_TYPES: (parts) => `You have duplicated questions in other types. Please ensure all questions are unique. This action can not be performed.\n\n${parts.join('\n\n')}`,
      EDIT_QUESTIONS: 'Edit questions',
      EDIT_OR_ADD_SUCCESS: 'The question has been added/updated successfully.',
      UPDATE_ERROR: 'There was a problem updating your questions. Please refresh and try again.',
      DATABASE_CONFLICT_ERROR: 'There was a conflict. Please update the questions one by one.',
      FETCH_ERROR: 'There was an error fetching the questions. Please try again.',
      EDIT_MODAL_LABELS: {
        QUESTION_OPTION_LABEL: (OPTS) => `Question ${OPTS}`,
      },
    },
    BASE_BUDGET_REALLOCATION: {
      TITLE: 'Base budget reallocation',
      EDIT_BASE_BUDGET_REALLOCATION: 'Edit base budget reallocation',
      UPDATE_SUCCESS: 'The question has been updated successfully.',
      NO_VALID_ADDITIONAL_QUESTION: 'No valid "Additional Questions" found.',
      FETCH_ERROR: 'There was an error fetching the additional question. Please try again.',
      UPDATE_ERROR: 'There was a problem updating the question. Please try again.',
    },
    REQUEST_PHASES: {
      TITLE: 'Request phases',
      REQUEST_LABEL: 'Request',
      REVIEW_LABEL: 'Review',
      UPDATE_SUCCESS: 'The budgeter phase has been updated successfully.',
      FETCH_PHASE_ERROR: 'There was an error fetching the budgeter phase. Please try again.',
      INVALID_PHASE_SELECTED: 'Invalid budgeter phase selected.',
      SET_PHASE_ERROR: 'There was an error setting the budgeter phase. Please try again.',
      EXPLANATION_TEXT: `The ${interpolate(REQUEST, ALIAS, nameConventions)} process has two phases - request
                phase and review phase. During the request phase department users can
                create ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)}. Once the review
                phase has started department users can NOT create{' '}
                ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)} anymore. Switching from{' '}
                request phase to review phase is not a one time action. It can be done
                multiple times. To go from one phase to another, please use the toggle
                below:`,
    },
    BUDGETER_FTE_CHANGES: {
      TITLE: 'FTE changes',
      FETCH_ERROR: 'There was an error fetching the budgeter FTE changes setting. Please try again.',
      UPDATE_SUCCESS: 'The budgeter FTE changes setting has been updated successfully.',
      UPDATE_ERROR: 'There was an error updating the budgeter FTE changes setting. Please try again.',
      EXPLANATION: 'By clicking this toggle on, the users will be able to change their number of items:'
    },
    MANAGE_ADMIN_USERS: {
      MAIN_TITLE: APP_ICONS.MANAGE_ADMIN_USERS,
      DELETE_DIALOG_TITLE: 'Delete user',
      DELETE_DIALOG_CONFIRMATION: 'Please note that action will delete all information about the user from all organizations and this cannot be undone.',
      DELETE_DIALOG_CONTENT: 'Are you sure you want to permanently delete this user from the system?',
      DELETE_DIALOG_TITLE_MULTIPLE: 'Delete users',
      DELETE_DIALOG_CONFIRMATION_MULTIPLE: 'Please note that action will delete all information about these users from all organizations and this cannot be undone.',
      DELETE_DIALOG_CONTENT_MULTIPLE: 'Are you sure you want to permanently delete these users from the system?',
      ADD_DIALOG_INLINE_MESSAGE_INFO: 'All organizations will be added by default to this user. You can remove organizations after you add this user.',
      EXISTING_EMAIL_ERROR: 'User with this email already exists',
      DELETE_SUCCESS: (OPTS) => `User: ${OPTS} is deleted!`,
      DELETE_FAILURE: 'Could not delete user!',
      DELETE_CURRENT_USER_ERROR: 'Cannot delete current user!',
      CANNOT_UNASSIGN_CURRENTLY_ACTIVE_ORG: 'You cannot unassign current active organization from yourself',
      UPDATE_SUCCESS: 'User updated!',
      UPDATE_FAILURE: 'Could not update user!',
      CREATE_SUCCESS: 'User created!',
      CREATE_FAILURE: 'Could not create user!',
      DELETE_MULTIPLE_SUCCESS: 'Users deleted!',
      DELETE_MULTIPLE_FAILURE: 'Could not delete users!',
      DELETE_SELF_FAILURE: 'Cannot delete current user!',
    },
    ORGANIZATION_HIERARCHY: {
      MAIN_TITLE: 'Organizational hierarchy',
      DELETE_DIALOG_TITLE: 'Delete level',
      DELETE_DIALOG_CONTENT: 'Please note that this action will delete all information about the level from all budgets and this cannot be undone.',
      DELETE_DIALOG_CONFIRMATION: 'Are you sure you want to permanently delete this level from the organizational hierarchy?',
      INTERNALLY_USED_ERROR: 'Internally used name, please use different one!',
      REGEX_AT_LEAST_ONE_LETTER: 'This field must contain at least one letter.',
      WHITE_SPACE_ERROR: 'The field is required',
      FUND_ERROR: 'Please keep in mind that Fund is already a part of your Cost Model',
      DUPLICATE_LEVELS: 'This level has a duplicate name with another level!',
      SUCCESS_HIERARCHY: 'Successfully updated organizational hierarchy',
      HIERARCHY_ERROR: 'Error occurred while saving hierarchy!',
      MAX_LEVELS: 'You have reached the maximum number levels.',
      DUPLICATE_ITEMS_DIALOG_MESSAGE: 'By deleting this level, the items will become duplicated. This action can not be performed.',
      ORGANIZATION_LEVELS_TITLE: 'Organizational levels',
      PERSONNEL_LEVELS_TITLE: 'Personnel categories',
      NON_PERSONNEL_LEVELS_TITLE: 'NonPersonnel categories',
      NO_STRUCTURE_MESSAGE: 'You don\'t have organizational hierarchy set up yet.',
      SETUP_INSTRUCTION: 'Click on the button below to set up your organizational hierarchy',
      MANAGE_BUTTON: 'Manage organizational hierarchy',
      DUPLICATE_ITEMS_DIALOG_TITLE: 'Duplicated items',
      INVALID_DATA: 'Invalid data. Please fix the errors and try again.',
      FETCH_ORGANIZATION_LEVELS_WITH_COST_CENTER_ERROR: `An error occurred while fetching organization levels with ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}. Please try again.`
    },
    NAME_CONVENTIONS: {
      MAIN_TITLE: APP_ICONS.NAME_CONVENTIONS,
      NAME_CONVENTIONS_SAVE_ERROR: 'An error occurred while updating name conventions. Something went wrong. Please try again.',
      NAME_CONVENTIONS_SAVE_SUCCESS: 'Editing name conventions was successful',
      INTERNALLY_USED_ERROR: 'Internally used name, please use different one!',
      CANNOT_SAVE_IF_HAVE_ERRORS: 'You still have errors. Please fix them before saving.'
    },
    CONFIGURE_BUDGET: {
      MAIN_TITLE: APP_ICONS.CONFIGURE_BUDGET,
      FETCH_ERROR: 'An error occurred while loading the configuration. Something went wrong. Please try again.',
      BUDGET_BASELINE_SAVING: 'New baseline budget successfully saved!',
      BUDGET_ACCESS_TYPE_SAVING: 'Updated access types successfully!',
      ORDER_BUDGETS_SAVING: 'Budget order saved successfully!',
      BUDGET_ORDER_NOT_AVAILABLE: 'Budget Order Not available. Something went wrong. Please try again later.',
      BUDGET_ORDER_NOT_SAVED: 'Could not save budget order. Something went wrong. Please try again.',
      BUDGET_ACCESS_PROBLEM_FETCHING: 'Problem with fetching all budgets with access type!',
      BUDGET_ACCESS_PROBLEM_SAVING: 'Problem with saving access type of budget!',
      BUDGET_BASELINE_PROBLEM_FETCHING: 'Problem with fetching budget data!',
      BUDGET_BASELINE_PROBLEM_SAVING: 'Problem with saving new budget baseline data!',
      NO_BUDGET_TITLE: 'No budget uploaded',
      NO_BUDGET_MESSAGE: 'Please upload a budget to proceed with the configurations.',
      NO_BUDGET_BUTTON: 'Upload budget',
    },  
    MANAGE_USER_ACCESS: {
      USER_ACCESS_UPDATE_FAILED: 'Failed to update user access',
      USER_ACCESS_UPDATED_SUCCESSFULLY: 'User access updated successfully',
      MAIN_TITLE: APP_ICONS.MANAGE_USER_ACCESS,
    },
    COST_ALLOCATION: {
      NO_ITEMS_FOUND: 'No items found.',
      NO_ROWS_TO_UPLOAD: 'No rows to submit. Please make sure you have action keyword "Add, Edit or Delete" on at least one row.',
      ACTIVE_BUDGET_NO_MATCH: 'The uploaded file does not match the active budget. Please upload a file that matches the active budget.',
      RECOMMENDATION_FETCH_ERROR: 'An error occurred while fetching costing recommendation.',
      FIX_MISTAKES_ERROR: 'Please fix the mistakes in the table before submitting.',
      ALLOCATIONS_UPDATE_SUCCESS: 'Allocations updated successfully.',
      ALLOCATIONS_UPDATE_ERROR: 'An error occurred while updating allocations.',
      RECOMMENDATION_MESSAGE: 'The recommended allocations are based on the following information: ',
      OVER_ALLOCATED_ERROR: 'The total allocation percent exceeds 100%. Please adjust the allocations before submitting.',
      ALREADY_AT_100: 'The total allocation percent is already 100% or more.',
      TITLES: {
        ALLOCATIONS_UPLOAD: 'Allocation upload',
        UPDATE_ALLOCATIONS: 'Update allocations',
        COST_ALLOCATION: 'Cost allocation',
        ALLOCATIONS: 'Allocations',
        PREDICTED_ALLOCATION_INFORMATION: 'Predicted allocation information',
      },
      NO_ALLOCATIONS_FOUND: 'No allocations found.',
      FTE_TOTAL_ZERO_ERROR: `The selected ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} have 0 FTE. Nothing has been changed.`,
      UPLOAD:{
        SUCCESS: 'Allocations have been successfully updated.',
        ERROR: 'An error occurred while updating allocations.',
        EXCEL_ERROR:{
          SHEET_COUNT: 'Please upload a file with 2 sheets',
          MISSING_SHEET_NAME: `Please upload a file with the first sheet named Allocations_${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}_cost and the second sheet named Allocations_${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}_revenue`
        },
        SHEET_VARIANTS: {
          ALLOCATION_PROGRAM_COST: `Allocation_${interpolate(PROGRAM, ALIAS, nameConventions)}_cost`,
          ALLOCATION_PROGRAM_REVENUE: `Allocation_${interpolate(PROGRAM, ALIAS, nameConventions)}_revenue`
        },
        LOADER_VALIDATING_MESSAGE: 'Validating allocations...',
      },
      PROBLEM_UPDATE_CHART_DATA: 'An error occurred while updating chart data. Please try again.',
    },
    DEPARTMENT_SCORING: {
      MAIN_TITLE: APP_ICONS.DEPARTMENT_SCORING,
      CANNOT_ACCESS_PAGE:(OPTS) => `Sorry, you can not access the department scoring page yet. You have to finish the ${OPTS} process first.`,
      LOADING_DATA_ERROR: 'An error occurred while loading data. Something went wrong. Please try again.',
      LOADING_SCORING_ERROR: 'An error occurred while loading the scoring. Something went wrong. Please try again.',
      FETCH_ERROR: 'There was an error trying to fetch data!',
      FETCH_PREDICTIONS_ERROR: 'There was an error trying to fetch scoring predictions!',
      STEP_SAVING_ERROR: 'An error occurred while saving the progress. Something went wrong. Please try again.',
      STEP_SAVING_SUCCESS: 'Your changes have been successfully saved.',
      FETCH_SCORING_ERROR: 'There was an error fetching these scoring recommendations.',
      NO_RESULT_IN_RESULT_TYPE:(OPTS) => `You do not have any ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} in the ${OPTS} ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} type. Please add one from the ${APP_ICONS.CONFIGURE_SCORING} page or proceed to the next step!`,
      NO_RESULT_TYPE: (OPTS) => `You do not have a ${OPTS} ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} type. Please add one from the ${APP_ICONS.CONFIGURE_SCORING} page or proceed to the next step!`,
      NO_PROGRAMS_FOUND: `No ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} found.`,
      DEFAULT_STEP: `Click on an item in the menu to score ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      ERROR_RECOMMENDATIONS_MISSING_PROGRAMS: `Please make sure you have ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} in your inventory and try again.`,
    },
    RECOMMEND_DIALOG: {      
      RECOMMEND_TITLE: 'Recommend scores',
      RECOMMEND_MESSAGE: 'Select score type(s) to generate. Note that only default BPA results can be scored through recommendations.',
    },
    DEPARTMENT_SCORING_REVIEW: {
      CANNOT_ACCESS_PAGE:(OPTS) => `Sorry, you can not access the department scoring page yet. You have to finish the ${OPTS} process first.`,
      FETCH_ERROR: 'There was an error trying to fetch data!',
      LOADING_PROGRAMS_ERROR: `There was a problem loading the ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}. NEW`,
      SAVE_SCORES_ERROR: 'There was a problem saving these scores!',
      SAVE_SCORES_SUCCESS: 'Your changes have been successfully saved.',
      MAIN_TITLE: APP_ICONS.DEPARTMENT_REVIEW,
      TITLE: `${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      LABELS: {
        COMMENT: 'Comment:',
        COMMENTS: 'Comments',
        DEPARTMENT_SCORE: 'Department score',
        FILTER: 'Filter',
      },
    },
    PEER_REVIEW: {
      MAIN_TITLE: APP_ICONS.PEER_REVIEW,
      EVALUATION_STEP: {
        CANNOT_ACCESS_PAGE: (OPTS) => `Sorry, you can not access the peer review page yet. You have to finish the ${OPTS} process first.`,
        PREPARE_ERROR: 'An error occurred while checking account data. Something went wrong. Please try again.',
        SAVE_PROGRESS_ERROR: 'An error occurred while saving the scores. Something went wrong. Please try again.',
        SAVING_ERROR_FROM_UNSAVED_CHANGES_DIALOG: 'An error occurred while saving the evaluation. Missing scores.',
        FEEDBACK_TEXT: `You have not been assigned to review any ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}.`,
        FETCH_SCORING_ERROR: 'There was an error fetching these scoring recommendations.',
        NO_PROGRAMS_FOUND: `No ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} found.`,
        FETCH_ASSIGNED_REVIEW_ERROR: 'An error occurred while loading assigned reviews. Something went wrong. Please try again.',
        SEARCH_TITLE: `Search by ${interpolate(PROGRAM, ALIAS, nameConventions)} name or description`,
        MISSING_SCORES_DIALOG_TITLE: 'Missing scores',
        MISSING_SCORES_DIALOG_MESSAGE: `Please fill in all missing scores for the following ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} before proceeding.`,
        PROGRAM_CARD_LABELS: {
          PEER_REVIEW_SCORE: 'Peer review score',
          PEER_REVIEW_COMMENT: 'Peer review comment',
          PROGRAM_INFO_LABELS: {
            SERVICE_TYPE: 'Service type',
            DEPARTMENT_SCORE: 'Department score',
            COMMENT: 'Comment',
          }
        }
      },
      SAVE_PROGRESS_SUCCESS: 'Your changes have been successfully saved.',
      DEPARTMENT_PREPARE_ERROR: `An error occurred while loading ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}. Something went wrong. Please try again.`,
      GET_SCORING_INFO_ERROR: 'An error occurred while getting scoring info. Something went wrong. Please try again.',
      SAVING_SCORES_ERROR: 'An error occurred while saving the scores. Something went wrong. Please try again.',
      EMPTY_STATE_TITLE: 'Sorry we couldn\'t find what you\'re looking for.',
      EMPTY_STATE_DESCRIPTION: 'Your user permissions doesn\'t currently include this page. Contact your supervisor if access is needed.',
      DEFAULT_STATE_IMAGE: `Click on an item in the menu to score ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      TEAM_RECONCILIATION_FETCH_ERROR: `An error occurred while loading team reconciliation ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}. Something went wrong. Please try again.`,
    },
    FINAL_REVIEW: {
      MAIN_TITLE: APP_ICONS.FINAL_REVIEW,
      TITLE: `${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
      VALIDATION_TITLE: 'Validation',
      CANNOT_ACCESS_PAGE:(OPTS) => `Sorry, you can not access the final review page yet. You have to finish the ${OPTS} process first.`,
      FETCH_ERROR: 'An error occurred while loading the cost model. Something went wrong. Please try again.',
      FETCH_PROGRAMS_ERROR: `Error occurred while fetching ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}`,
      FETCH_QUARTILE_ERROR: 'An error occurred while getting default service type boundaries. Something went wrong. Please try again.',
      FETCH_SCORING_ERROR: 'There was an error fetching these scoring recommendations.',
      FIX_MISTAKES_ERROR: 'Please fix the mistakes in the table before submitting.',
      SAVE_QUARTILE_ERROR: 'An error occurred while saving service type boundaries. Something went wrong. Please try again.',
      SCORES_APPLIED: 'Scores have been successfully applied.',
      APPLYING_SCORES_ERROR: 'There was a problem applying these scores!',
      QUARTILE_DIALOG_TITLE: 'Quartile boundaries',
      SAVE_SCORES_ERROR: 'There was a problem saving these scores!',
      SAVE_SCORES_SUCCESS: 'Your changes have been successfully saved.',
      UPLOAD_SCORES_ERROR: 'Error while trying to upload scores.',
      PARSE_FILE_ERROR: 'Error while trying to parse the file.',
      QUARTILE_INLINE_MESSAGE: 'By changing the quartile boundaries, all scores will be recalculated and this may result in quartile change.',
      ASCENDING_ORDER: 'The numbers should be in ascending order.',
      UPLOAD_DIALOG: {
        TITLE: 'Score upload',
        DOWNLOAD: 'Download scores',
        INFO: 'Please upload one Excel file.'
      },
      VALIDATION_ERRORS: {
        ACTION_EDIT: 'Action(Edit) must be "Edit".',
        RESULT_TYPE: (OPTS) => `Please specify one of the following values: ${OPTS.join(', ')}`,
        RESULT_ABBR: (OPTS) => `Please specify one of the following values: ${OPTS[0][OPTS[1][`${interpolate(PRIORITY, ALIAS, nameConventions)} type`]].join(', ')}`,
        SCORE_NUMBER: (OPTS) => `Please enter a number for the ${OPTS}.`,
        SCORE_ONE_OF_FOLLOWING: (OPTS) => `Please enter a number from one of the following: ${OPTS.map(scoreOption => scoreOption.weight).join(', ')}`,
        ID_REQUIRED: (OPTS) => `${OPTS} is required.`,
        MISSING_SCORES_SHEET: 'Scores sheet must exist in your file',
        EMPTY_FILE: 'Empty file',
        BOTH_SCORES_MISSING: 'At least one of Department Score or Final Score must be provided.',
        WRONG_RESULT_ID: `Wrong ${interpolate(PRIORITY, ALIAS, nameConventions)} id for current budget`
      }
    },
    VALIDATION_REQUIREMENTS: {
      PASSWORD: 'Password is case sensitive and must include 1 uppercase letter, 1 lowercase letter, and 1 number.  Additionally, symbols are supported but not Required, white spaces are not supported',
      EMAIL: 'Enter valid email address (e.g. example@gmail.com)',
      CONFIRM_PASSWORD: 'Passwords do not match',
      EMPTY_VALUE: REQUIRED_FIELD_TEXT
    },
    BUDGET_SUMMARY: {
      TITLE: 'Budget summary',
      INLINE_MESSAGE: `Page is not affected by ${interpolate(USER_GROUP, ALIAS, nameConventions)} selection.`,
      IMAGE_TITLE_MESSAGE: 'No budget uploaded',
      IMAGE_SUB_TITLE_MESSAGE: 'Please upload a budget',
      UPLOAD_BUTTON: 'Upload budget',
      GENERAL: {
        FETCH_ERROR: 'Budget summary not available. Something went wrong. Please try again.',
        NO_COST_MODEL: 'No cost model found. Please create or select a cost model first.',
        TEMPLATE_DOWNLOAD_ERROR: 'Failed to download the budget template. Please try again.',
      },
      DELETE: {
        SUCCESS: 'The budget has been deleted successfully.',
        ERROR: 'An error occurred while deleting the budget. Something went wrong. Please try again.',
        ACTIVE: 'The currently active budget cannot be deleted!',
        DIALOG_CONTENT: 'Deleting a budget will permanently remove it and all associated data from the system.',
        CONFIRMATION: (OPTS) => `Are you sure you want to delete ${OPTS[0]} - ${OPTS[1]}?`
      },
      MENU: {
        DOWNLOAD: 'Download budget',
        COPY: 'Copy budget',
        RENAME: 'Edit budget name',
        PUBLISH: 'Publish to Present PBB',
        DELETE: 'Delete budget'
      },
      COPY: {
        MISSING_NAME_OR_YEAR: 'This budget is missing either a name or a year. It can not be copied without a name or a year.',
        ALREADY_EXISTS: 'There is already a budget with the same name and year. Please change it and try again!',
        SUCCESS: 'The new budget has been created successfully.',
        ERROR: 'There was an error creating the new budget. Please try again.',
        ALLOCATIONS: 'Allocations',
        ALLOCATIONS_AND_REQUESTS: `Allocations and ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)}`,
        GET_REPORT: 'Get report',
        OVER_ALLOCATION_ERROR: 'There are overallocated items. Please adjust before attempting to copy!'
      },
      COST_CENTER: {
        FETCH_ERROR: `There was an error fetching ${interpolate(USER_GROUP, ALIAS, nameConventions)} summary. Please try again.`,
        TITLE: `${interpolate(USER_GROUP, ALIAS, nameConventions)} summary`
      },
      RENAME: {
        MISSING_NAME_OR_YEAR: 'Budget is missing either a name or a year. It can not be renamed without a name or a year.',
        ALREADY_EXISTS: 'There is already a budget with the same name and year. Please change it and try again!',
        SUCCESS: 'The budget has been renamed successfully.',
        ERROR: 'There was an error renaming the budget. Please try again.'
      },
      PUBLISH: {
        SUCCESS: 'The budget has been published to Present PBB successfully.',
        ERROR: 'There was an error publishing the budget to Present PBB. Please try again.'
      }
    },
    BUDGET_UPLOAD: {
      TITLE: 'Budget upload',
      GENERAL: {
        NO_ORG_HIERARCHY: 'Complete organizational hierarchy first.',
        SUCCESS: 'Successfully uploaded budget',
        ERROR: 'Budget upload failed. Please check your data and try again',
        NO_DATA_TO_UPLOAD: 'No data to upload.',
        INCOMPLETE_PROCESS: 'Process not finished. You can only save on the final step.',
        REDIRECTION_INCOMPLETE_PROCESS: 'Couldn\'t save upload. You can only save on the final step.',
        CORRECT_ERRORS: 'Please correct the errors before submitting.',
        REDIRECTION_CORRECT_ERRORS: 'Couldn\'t save upload. There were unresolved errors in the data.',
      },
      PARSING: {
        NO_FILE: 'No file provided.',
        UNSUPPORTED_FORMAT: 'Unsupported file format.',
        CSV_MISSING_TYPE: 'CSV file missing isPersonnel / isNonPersonnel',
        INVALID_CSV: 'Invalid CSV parsing',
        NO_SHEET_DATA: 'Excel file has no sheets with Personnel or NonPersonnel data.'
      },
      MAPPING: {
        FETCH_ERROR: 'Failed to fetch mappings',
        SAVE_SUCCESS: 'Successfully saved mapping',
        SAVE_ERROR: 'Failed to save mapping',
        COLORED_ROW: 'Colored row must be mapped manually',
        SAVE_MAPPING_TITLE: 'Save mapping',
        MAPPING_MESSAGE: 'On the second row, please use the dropdown to select the spreadsheet column that corresponds to the required field.',
        AUTODETECT_MAPPING: 'Autodetect mapping'
      },
      VALIDATION: {
        FAIL_VALIDATION: 'Fail validation',
        PASS_VALIDATION: 'Pass validation',
        NOT_SAME_BUDGET: (OPTS)=> `Only one budget can be edited per Excel file. Please change budget and year to ${OPTS}`,
        FRONT_END_DUPLICATE: 'Duplicate item. This combination must be unique: budget name, year, account code, duration, fund, organization levels and object categories.',
      },
      SAVE_MAPPING_MODAL: {
        TITLE: 'Save mapping',
        NAME_LABEL: 'Mapping name',
        MAPPING_NAME_ALREADY_EXISTS: 'Mapping name already exists'
      },
      SHOW_UNMAPPED_MODAL: {
        TITLE: 'Mapping error',
        DESCRIPTION: 'All columns must be mapped. The following columns are not mapped:',
        ALL_MAPPED: 'All columns are mapped.'
      },
      
    },
    MANAGE_USERS: {
      MAIN_TITLE: APP_ICONS.MANAGE_USERS,
      UPLOAD_TITLE: 'User upload',
      USER_ALREADY_EXISTS:(OPTS)=> `This user already exists. Please, change the email to ${OPTS}!`,
      USER_CREATED: 'New user successfully created',
      CREATE_USER_SUCCESS: 'User was successfully created!',
      CREATE_USER_ERROR: 'Could not create user.',
      CREATE_USER_WITH_DIFFERENT_ORGANIZATION_KEY_ERROR: 'User is part of different client. Users can not be added to different clients due to login complications. Check with administer.',
      UPDATE_USER_SUCCESS: 'User has been successfully updated!',
      UPDATE_USER_ERROR: 'Could not update user.',
      DELETE_USER_SUCCESS: 'User has been successfully deleted!',
      DELETE_USER_ERROR: 'Could not delete user.',
      DELETE_MULTIPLE_USERS_SUCCESS: 'Users deleted!',
      DELETE_MULTIPLE_USERS_ERROR: 'Could not delete users.',
      CANNOT_DELETE_SELF: 'Cannot delete current user!',
      USER_GROUP_ASSIGNMENT: {
        USER_GROUP_ASSIGNMENT_TITLE: `Edit ${interpolate(USER_GROUP, ALIAS, nameConventions)} assignment`,
        USER_GROUP_DELETE_SUCCESS: `${interpolate(USER_GROUP, ALIAS, nameConventions)} deleted successfully!`,
        USER_GROUP_DELETE_ERROR: `Could not delete ${interpolate(USER_GROUP, ALIAS, nameConventions)}!`,
        DELETE_DIALOG_TITLE: `Delete ${interpolate(USER_GROUP, ALIAS, nameConventions)}`,
        DELETE_DIALOG_CONTENT: `Are you sure you want to permanently delete this ${interpolate(USER_GROUP, ALIAS, nameConventions)} from the organization?`,
        DELETE_DIALOG_CONFIRMATION: `Please note that this action will delete all information about the ${interpolate(USER_GROUP, ALIAS, nameConventions)} from the current organization and this cannot be undone.`,
      },
      USER_UPLOAD_SUCCESS: 'Users uploaded successfully',
      USER_UPLOAD_ERROR: 'Users upload failed',
      SUPER_USERS_INFO: `Super users will be automatically assigned to all ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)} despite the selection.`
    },
    NAVIGATION: {
      SETTING_SELECTED_USER_GROUPS_ERROR: `Error while trying to set selected ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}.`,
      FETCHING_USER_GROUPS_ERROR: `Error while trying to fetch ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}.`
    },
    DOCUMENT_GENERATOR: {
      DOCUMENT_GENERATOR_CONNECTION_ERROR: 'Couldn`t connect to the document generator service! Please try again later.',
      DOCUMENT_GENERATOR_PREVIEW_ERROR: 'Error occurred while generating PDF preview!',
      DOCUMENT_GENERATOR_DOCUMENT_ERROR:(OPTS) => `Error occurred for: ${OPTS}!`,
      NO_GENERATED_DOCUMENTS: 'No reports available yet!',
      GENERATING_REPORT: 'Generating report...',
      DATE_GENERATED: 'Date created: ',
    },
    LOGIN: {
      USERNAME_VALIDATION_ERROR: 'Username must be at least 3 characters long',
    },
    HOME_PAGE: {
      WELCOME_USER: (OPTS) => `Welcome, ${OPTS?.firstName} ${OPTS?.lastName}!`,
    },
    USER_PROFILE: {
      USER_ALREADY_EXISTS: 'User with that email already exist.',
      USER_UPDATE_SUCCESS: 'Successfully saved changes.',
      USER_UPDATE_ERROR: 'An error occurred while trying to save changes. Please try again.',
      NAME_VALIDATION_MESSAGE: REQUIRED_FIELD_TEXT,
      CHANGE_PASSWORD: 'Change password',
      PASSWORD_CONDITIONS: 'Password is case sensitive and must include 1 uppercase letter, 1 lowercase letter, and 1 number.  Additionally, symbols are supported but not Required, white spaces are not supported',
      PASSWORD_CHANGE_SUCCESS: 'Successfully changed password.'
    },
    DATA_VISUALIZATIONS: {
      MAIN_TITLE: APP_ICONS.DATA_VISUALIZATION,
      NO_PROGRAMS_IN_SELECTED_BUDGET: `No ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} in the selected budget.`,
      NO_DATA_TO_DISPLAY: 'No data to display.',
      NO_DATA_TO_IN_RANGE: 'No data to display in the selected range.',
      CHOOSE_TWO_BUDGETS_TO_COMPARE: `Choose two budgets to compare and see the allocation changes between ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}.`,
      DATA_PULLS_EXPLANATION: 'This data pulls from allocated costs. If costs are not 100% allocated, you will only see the total allocated amount of a given position/line item.',
      NO_RESULTS: `Sorry, there are no ${interpolate(PRIORITY,PLURAL_FORM_ALIAS, nameConventions)} in the selected budget. Please go to Edit ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} (Super Users only).`,
      COMPARE_RESULTS_MESSAGE: `To compare two ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} they must have the same scale.`,
      NO_PROGRAMS_FOUND: `No ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} found.`,
      NO_RESULTS_FOUND: `No ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} found.`,
      NO_SCORES_FOUND: 'No scores found.',
      NO_BUDGETS_FOUND: 'No budgets data found.',
      NO_FUNDS_FOUND: 'No funds data found.',
      NO_BUDGETS_FOR_COMPARISON_SELECTED: {
        TITLE: 'No data yet.',
        MESSAGE: 'Please select budgets for comparison.'
      },
      REDIRECTION_BUTTON: `Create ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions).toLowerCase()}`,
      NO_COST_TYPES_FOUND: (OPTS) => `No ${OPTS} cost types data found.`,
      NO_ALLOCATIONS_FOUND: 'No allocation has been made',
      NO_BUDGETS_FOUND_REDIRECT: 'Upload budget',
      NO_ALLOCATIONS_FOUND_REDIRECT: 'Allocate costs',
      NO_RESULTS_FOUND_REDIRECT: `Create ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)?.toLowerCase()}`,
      NO_SCORES_FOUND_REDIRECT: 'Create scores',
      PROBLEM_RESULT_SPENDING_PREPARE: 'There was a problem preparing the result spending data. Please try again.',
      BUDGET_FUND_COMPARE_PROBLEM: 'There was a problem preparing the budget fund comparison data. Please try again.',
    },
    CONFIGURE_SCORING: {
      MAIN_TITLE: APP_ICONS.CONFIGURE_SCORING,
      EDIT_RESULT_TITLE: (OPTS) => `Edit ${OPTS} ${interpolate(PRIORITY, ALIAS, nameConventions)}`,
      ADD_RESULT_TITLE: `Add ${interpolate(PRIORITY, ALIAS, nameConventions)}`,
      RESULT_TYPE_TITLE: `Delete ${interpolate(PRIORITY, ALIAS, nameConventions)}`,
      RESULT_TYPE_DELETE_MAIN_MESSAGE: `Are you sure you want to permanently delete this ${interpolate(PRIORITY, ALIAS, nameConventions)} from the scoring process?`,
      RESULT_TYPE_DELETE_MESSAGE: `Please note that this action will delete all information about the ${interpolate(PRIORITY, ALIAS, nameConventions)} from all budgets and this cannot be undone.`,
      CONFIGURE_SCORING_DELETE_TITLE: `${DELETE} ${interpolate(PRIORITY, ALIAS, nameConventions)} type`,
      CONFIGURE_SCORING_MAIN_MESSAGE: `Are you sure you want to permanently delete this ${interpolate(PRIORITY, ALIAS, nameConventions)} type from the scoring process?`,
      CONFIGURE_SCORING_EDIT_MESSAGE: 'Changing the scale or toggling the "Have score" toggle will result in deleting all the scores for this result.',
      CONFIGURE_SCORING_MESSAGE: `Please note that this action will delete all information about the ${interpolate(PRIORITY, ALIAS, nameConventions)} type and this cannot be undone.`,
      CONFIGURE_SCORING_CREATE_TITLE: `${CREATE} ${interpolate(PRIORITY, ALIAS, nameConventions)} type`,
      RESULT_TYPE_SUCCESS: (OPTS) => `Successfully ${OPTS} ${interpolate(PRIORITY, ALIAS, nameConventions)} type!`,
      RESULT_TYPE_ERROR: (OPTS) => `There was an error trying to ${OPTS} ${interpolate(PRIORITY, ALIAS, nameConventions)} type!`,
      RESULT_SUCCESS: (OPTS) => `Successfully ${OPTS} ${interpolate(PRIORITY, ALIAS, nameConventions)}!`,
      RESULT_ERROR: (OPTS) => `There was an error trying to ${OPTS} ${interpolate(PRIORITY, ALIAS, nameConventions)}!`,
      EDIT_DUPLICATE_CONFIGURATION_ERROR: 'A result with the same configuration already exists!',
      SAVE_SCORING_RECOMMENDATION_ERROR: 'There was an error trying to save the scoring recommendation toggle!',
      PREPARE_ERROR: `There was an error trying to fetch all ${interpolate(PRIORITY, ALIAS, nameConventions)} types!`,
      DEFINITIONS_INFO: 'If the "Have score" toggle is off, one definition is required. If the toggle is on, the number of definitions must match the number of scale points.',
      DUPLICATE_TITLE: 'Duplicate result',
      DUPLICATE_CONTENT: `${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} must have unique abbreviation/name pair!`,
      ABBREVIATIONS_TITLE: 'Wrong abbreviations',
      ABBREVIATIONS_CONTENT: `The suggested ${interpolate(PROGRAM, ALIAS, nameConventions)} attributes are available for **Mandate, Reliance, Demand, Cost Recovery and Population Served**. Please update your abbreviations to match all of them and have the "Have score" toggle on to get the suggested data.`,
      SUGGESTION_TITLE: 'Changes suggestion status',
      SUGGESTION_CONTENT: `Since a new ${interpolate(PRIORITY, ALIAS, nameConventions)} was added, the AI suggestions for the BPA ${interpolate(PRIORITY, ALIAS, nameConventions)} can not work.`,
    },
    BUDGET_REQUESTS: {
      MAIN_TITLE: APP_ICONS.BUDGET_REQUESTS,
      NO_BUDGET: `Budget ${interpolate(REQUEST, PLURAL_FORM_ALIAS,nameConventions)} not available. Please complete the budget upload process.`,
      FETCH_ERROR: 'Sorry, something went wrong. Please try again later.',
      GROUP_REQUESTS_DIALOG_TITLE: `Group ${interpolate(REQUEST, PLURAL_FORM_ALIAS,nameConventions)}`,
      LINE_ITEMS_DIALOG_TITLE: (OPTS) =>  getLineItemsDialogTitle(OPTS),
      FILTERS_APPLIED: 'Your filters are applied',
      CREATE_REQUEST_GROUP_SUCCESS_MESSAGE: `${interpolate(REQUEST, PLURAL_FORM_ALIAS,nameConventions)} grouped successfully!`,
      CREATE_REQUEST_GROUP_ERROR_MESSAGE: `There was an error trying to group ${interpolate(REQUEST, PLURAL_FORM_ALIAS,nameConventions)}!`,
      ERROR_FETCHING_REQUEST: `An error occurred while fetching the ${interpolate(REQUEST, ALIAS,nameConventions)} data.`,
      ERROR_FETCHING_LINE_ITEM: 'An error occurred while fetching the line item data.',
      SUMMARY_TITLES:{
        APPROVED_ALIGNMENTS: 'Approved amounts to all alignments',
        REALLOCATION_OPPORTUNITIES  : 'Reallocation opportunities',
        REQUESTED_VS_APPROVED: 'Amount requested vs approved'
      },
      UNABLE_TO_SAVE_YET: 'Proceed to the last step to save changes',
      PROGRESS_RESET: 'Request progress has been reset, no changes were saved',
      EDIT_MODE: 'Custom values',
      REQUESTER: {
        INCREASE:{
          TITLE: `Increase item cost ${interpolate(REQUEST, ALIAS, nameConventions)}`,
          COST_MANIPULATOR_TITLE: 'Increase',
          COST_FIELD_SUPPORT_TEXT: 'New total item cost cannot be less or equal than current total item cost',
        },
        DECREASE: {
          TITLE: `Decrease item cost ${interpolate(REQUEST, ALIAS, nameConventions)}`,
          COST_MANIPULATOR_TITLE: 'Decrease',
          COST_FIELD_SUPPORT_TEXT: 'New total item cost cannot be greater or equal than current total item cost',
        },
        ['ADD-EXISTING']: {
          TITLE: `New item ${interpolate(REQUEST, ALIAS, nameConventions)}`,
        },
        FLOW_DESCRIPTION_TITLE: `${interpolate(REQUEST, ALIAS, nameConventions)} description`,
        DURATION_TYPE_LABEL: `${interpolate(REQUEST, ALIAS,nameConventions)} duration type`,
        ADDITIONAL_FIELDS_BUTTON_TEXT: (OPTS) => `${OPTS} additional fields`,
        BOX_CHOICES_TITLE: 'Box choices',
        TYPE_TO_ALLOCATE_MESSAGE: 'Select a type to allocate',
        MANUAL_ALLOCATION_NO_AMOUNT_ERROR: REQUIRED_FIELD_TEXT,
        MANUAL_ALLOCATION_AMOUNT_NOT_ENOUGH_COST_ERROR: 'Not enough allocated cost',
        MANUAL_ALLOCATION_AMOUNT_ERROR: 'Error. Cost is higher than available amount.',
        MANUAL_ALLOCATION_ADD_REMAINING: 'Add remaining',
        MANUAL_ALLOCATION_NO_SELECTED_ALLOCATION: 'No selected allocation',
        MANUAL_ALLOCATION_NOT_ENOUGH_REMAINING_AMOUNT: 'Not enough remaining amount',
        MANUAL_ALLOCATION_CANNOT_APPLY_REMAINING: 'Cannot apply',
        MAIN_TITLE: 'Selected item',
        MAIN_TITLE_TOOLTIP: 'There is no name for this item',
        ERROR_CREATING_REQUEST: `An error occurred while processing your ${interpolate(REQUEST, ALIAS, nameConventions)}.`,
        SUCCESS_DIALOG_TITLE: (OPTS) => `You have successfully ${OPTS? 'updated': 'created'} a ${interpolate(REQUEST, ALIAS, nameConventions)}!`,
        SUCCESS_DIALOG_MESSAGE: `Would you like to view your ${interpolate(REQUEST, ALIAS, nameConventions)}?`,
        VIEW_REQUEST: `View ${interpolate(REQUEST, ALIAS, nameConventions)}`,
        ADJUST_REQUESTED_AMOUNT_TOOLTIP: 'Adjust the requested amount',
        MISSING_DESCRIPTION_TOOLTIP: 'Enter description',
        MISSING_OFFSET_TOOLTIP: 'Choose if this recommendation be offset by a budget reduction',
        NO_ROW_SELECTED: 'Select a row below to edit its allocation',
      },
      DELETE_REQUEST_PROMPT_TITLE: `Delete ${interpolate(REQUEST, ALIAS, nameConventions)}`,
      DELETE_REQUEST_PROMPT_TITLE_MULTIPLE: `Delete ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)}`,
      DELETE_REQUEST_PROMPT_CONTENT: `Are you sure you want to permanently delete this ${interpolate(REQUEST, ALIAS, nameConventions)} from the system?`,
      DELETE_REQUEST_PROMPT_CONTENT_MULTIPLE: `Are you sure you want to permanently delete these ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)} from the system?`,
      DELETE_REQUEST_PROMPT_MESSAGE: `Please note that this action will delete all information about the ${interpolate(REQUEST, ALIAS, nameConventions)} and this cannot be undone.`,
      DELETE_REQUEST_PROMPT_MESSAGE_MULTIPLE: `Please note that this action will delete all information about the ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)} and this cannot be undone.`,
      DELETE_REQUEST_SUCCESS_MESSAGE: `${interpolate(REQUEST, ALIAS, nameConventions)} deleted successfully.`,
      DELETE_REQUEST_SUCCESS_MESSAGE_MULTIPLE: `${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)} deleted successfully.`,
      DELETE_REQUEST_FAILED_MESSAGE: 'Sorry, something went wrong. Please try again later.',
      APPROVE_REQUEST_GENERIC_ERROR: 'Sorry, something went wrong. Please try again later.',
      APPROVE_AND_COMMENT_REQUEST_TITLE: `Approve ${interpolate(REQUEST, ALIAS, nameConventions)}`,
      APPROVE_AND_COMMENT_REQUEST_MESSAGE: 'Would you like to leave a decision comment for this approval?',
      APPROVE_REQUEST_CONFIRM_BUTTON_TEXT: 'Approve',
      DECISION_COMMENT_TEXTAREA_LABEL: 'Decision comment',
      REQUEST_ACTION_DUPLICATED_ITEMS_ERROR: `An item with this hierarchy already exists in the current budget. To make an item unique one of the following
        fields must be unique for the item: AcctCode, Account type, Duration type, Fund, Organizational hierarchy and Object Category Levels.
        Please, change some of them and try again in order to continue the process
        of creating a ${interpolate(REQUEST, ALIAS, nameConventions)}.`,
      REQUEST_ACTION_ALLOCATION_ISSUE: (action, total, failed) => { 
        return `
          ${failed} of ${total} ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)} were not ${action}.
          This may be due to invalid allocations (negative or over 100%) or because the item does not exist.
        `;
      },
      REQUEST_ACTION_SUCCESS_MESSAGE: (action, isMultiple) => {
        return `You have successfully ${action} selected ${isMultiple? interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions) : interpolate(REQUEST, ALIAS, nameConventions)}.`;
      },
      GROUP_ACTION_SUCCESS_MESSAGE: (action, isMultiple) => {
        return `You have successfully ${action} selected ${isMultiple? 'groups' : 'group'}.`;
      },
      REQUEST_GROUPS_ACTION_ALLOCATION_ISSUE: (action, total, failed) => { 
        return `
          ${failed} of ${total} ${total > 1 ? 'groups' : 'group'} were not ${action}.
          This may be due to invalid allocations (negative or over 100%) or because some items do not exist.
        `;
      },
      CONVERT_TO_PENDING_REQUEST_TITLE: `Convert ${interpolate(REQUEST, ALIAS, nameConventions)} to pending`,
      CONVERT_TO_PENDING_REQUEST_MESSAGE: 'Would you like to leave a decision comment for this approval?',
      CONVERT_TO_PENDING_REQUEST_CONFIRM_BUTTON_TEXT: 'Convert to pending',
      REJECT_REQUEST_TITLE: `Reject ${interpolate(REQUEST, ALIAS, nameConventions)}`,
      REJECT_REQUEST_MESSAGE: 'Would you like to leave a decision comment for this rejection?',
      REJECT_REQUEST_CONFIRM_BUTTON_TEXT: 'Reject',
      GROUP_SUCCESSFULLY_UNGROUP: 'Group was successfully removed',
      GROUP_ERROR_UNGROUP: 'Problem removing group.',
      GROUP_SUCCESSFULLY_APPROVED: 'Group was successfully approved',
      GROUP_APPROVED_ERROR: 'Problem approving groups.',
      GROUP_SUCCESSFULLY_REJECTED: 'Group was successfully rejected',
      GROUP_REJECTED_ERROR: 'Problem rejecting groups.',
      GROUP_SUCCESSFULLY_CONVERTED_TO_PENDING: 'Group was successfully converted to pending',
      ACTIVATE_BUDGET_TITLE: 'Activate budget',
      ACTIVATE_BUDGET_PRIMARY_BUTTON: 'Activate',
      ACTIVATE_BUDGET_MESSAGE: 
        `This action results in creating two new budgets with approved changes. 
        The first budget will contain all of the items, whilst the second will only contain 'Ongoing' items and NO 'Onetime' items. 
      `,
      ACTIVATE_BUDGET_SUCCESS_MESSAGE: 'Your budget has been successfully activated',
      ACTIVATE_BUDGET_ERROR_MESSAGE: 'Problem with activating the budget.',
      ACTIVATION_BUDGET_NAME_LABEL: 'Budget name',
      ACTIVATION_BUDGET_YEAR_LABEL: 'Budget year',
      ACTIVATION_FAILED_DUE_TO_MISSING_BUDGET: 'Current budget not found',
      ACTIVATION_FAILED_DUE_TO_PENDING_REQUESTS: `Cannot activate budget with pending ${interpolate(REQUEST, PLURAL_FORM_ALIAS, nameConventions)}`,
      ACTIVATION_FAILED_DUE_TO_NAMING_ISSUE: 'Budget with this name already exists',
      ACTIVATION_FAILED_DUE_TO_PROPOSED_NAMING_ISSUE: 'Proposed budget with this name already exists',
      REQUEST_INFO: {
        TITLE: 'Item that you selected',
        SUBTITLE: `${interpolate(REQUEST, ALIAS, nameConventions)} information`,
        APPROVE_BUTTON_TITLE: 'Approve',
        REJECT_BUTTON_TITLE: 'Reject',
        CONVERT_TO_PENDING_BUTTON_TITLE: 'Convert to pending',
        DELETE_REQUEST_TITLE: 'Delete',
        EDIT_REQUEST_TITLE: `Edit ${interpolate(REQUEST, ALIAS, nameConventions)}`,
        TAB_MAIN_INFORMATION: 'Main information',
        TAB_BUDGET_IMPACT: 'Budget impact',
        TAB_REQUEST_QUESTIONS: `${interpolate(REQUEST, ALIAS, nameConventions)} questions`,
        TAB_COMMENTS: 'Decision comments',
        TAB_MAIN_INFO: {
          COST_TYPE_LABEL: 'Cost type',
          DURATION_TYPE_LABEL: 'Duration type',
          DURATION_TYPE_VALUE_ONGOING: 'Ongoing',
          DURATION_TYPE_VALUE_ONETIME: 'One time',
          ACCOUNT_CODE_LABEL: 'AcctCode',
          ACCOUNT_TYPE_LABEL: 'Account type',
          ACCOUNT_TYPE_VALUE_EXPENSE: 'Expense',
          ACCOUNT_TYPE_VALUE_REVENUE: 'Revenue',
          FUND_LABEL: 'Fund',
          COST_CENTER_LABEL: `${interpolate(USER_GROUP, ALIAS, nameConventions)}`,
          ORGANIZATION_HIERARCHY_LEVELS_LABEL: 'Organizational hierarchy levels',
          CATEGORY_LEVELS_LABEL: 'Category levels',
          DECISION_COMMENT_MADE_BY: 'Decision comment made by',
          LAST_UPDATED_LABEL: 'Last updated',
          REQUEST_DURATION_TYPE_LABEL: `${interpolate(REQUEST, ALIAS, nameConventions)} duration type`,
          CURRENT_AMOUNT_LABEL: 'Current amount',
          REQUESTED_AMOUNT_LABEL: 'Requested amount',
          APPROVED_AMOUNT_LABEL: 'Approved amount',
          TO_BE_APPROVED_ONGOING_AMOUNT_LABEL: 'To be approved ongoing amount',
          TO_BE_APPROVED_ONETIME_AMOUNT_LABEL: 'To be approved one time amount',
          NEW_TOTAL_COST_LABEL: 'New total item cost',
          ONGOING_AMOUNT_LABEL: 'Ongoing amount',
          ONETIME_AMOUNT_LABEL: 'One time amount',
          EDIT_META_ITEM_TITLE: 'Edit item',
          EDIT_META_ITEM_SUCCESS: `${interpolate(REQUEST, ALIAS, nameConventions)} item updated successfully.`,
          EDIT_META_ITEM_ERROR: `Error updating the ${interpolate(REQUEST, ALIAS, nameConventions)} item.`,
          PARTIAL_APPROVAL_DECREASING_UNDER_ZERO_ERROR: 'The money for approval can not be more than the current item total cost.',
          PARTIAL_APPROVAL_OVERAPPROVE_ERROR: 'The money for approval can not be more than the requested amount.',
          OLD_NUMBER_OF_ITEMS_LABEL: 'Old number of items',
          NEW_NUMBER_OF_ITEMS_LABEL: 'New number of items',
          REQUEST_DESCRIPTION_LABEL: `${interpolate(REQUEST, ALIAS, nameConventions)} description`,
        },
        TAB_QUESTIONS_AND_ANSWERS: {
          BOX_CHOICES_TITLE: 'Box choices',
          NO_BOX_CHOICES_TITLE: 'No box choices',
          GENERAL_QUESTIONS_TITLE: 'General questions',
          NO_GENERAL_QUESTIONS_TITLE: 'No general questions answers',
          PERSONNEL_QUESTIONS_TITLE: 'Personnel questions',
          NONPERSONNEL_QUESTIONS_TITLE: 'Non Personnel questions',
          NO_PERSONNEL_QUESTIONS_TITLE: 'No personnel questions answers',
          NO_NONPERSONNEL_QUESTIONS_TITLE: 'No non personnel questions answers',
          EDIT_ANSWERS: 'Edit answers',
          ERROR_EDIT_ANSWERS_FAILED: 'Edit answers failed! Please try again later',
          ATTACHMENTS_TITLE: 'Attachments',
          PROBLEM_DOWNLOAD_FILE: 'There was a problem downloading. Please try again later.',
        },
        TAB_DECISION_COMMENTS: {
          TEXTAREA_TITLE: 'Enter your decision comment below',
          TEXTAREA_HELPER_TEXT: 'Decision comment',
          TEXTAREA_CLEAR: 'Clear',
          ADD_COMMENT_BUTTON: 'Add comment',
          EDIT_COMMENT_BUTTON: 'Edit comment',
          PENDING_STATUS: 'Pending',
          APPROVED_STATUS: 'Approved',
          REJECTED_STATUS: 'Rejected',
          COMMENT_STATUS: 'Comment',
          UPDATED_ON: 'Updated on:',
          COMMENT_SAVE_SUCCESS: 'Decision comment has been saved successfully',
          DELETE_REQUEST_COMMENT_PROMPT_TITLE: 'Delete comment',
          DELETE_REQUEST_COMMENT_PROMPT_CONTENT: `Are you sure you want to permanently delete this ${interpolate(REQUEST, ALIAS, nameConventions)} comment from the system?`,
          DELETE_REQUEST_COMMENT_PROMPT_MESSAGE: `Please note that this action will delete all information about the ${interpolate(REQUEST, ALIAS, nameConventions)} comment and this cannot be undone.`,
          DELETE_REQUEST_COMMENT_SUCCESS_MESSAGE: `${interpolate(REQUEST, ALIAS, nameConventions)} comment deleted successfully.`,
        }
      },
    },
    MANAGE_ORGANIZATION_ACCESS: {
      MAIN_TITLE: APP_ICONS.MANAGE_ORGANIZATION_ACCESS,
      ORGANIZATION_ACCESS_UPDATE_FAILED: 'Failed to update organization access',
      ORGANIZATION_ACCESS_UPDATED_SUCCESSFULLY: 'Organization access updated successfully',
    },
    BUDGET_LINE_ITEM_EDITOR: {
      SUCCESSFULLY_SAVED: 'Your changes have been successfully saved.',
      ERROR_SAVING: 'There was a problem updating the budget line item. Please try again later.',
      TITLE: 'Budget line item editor',
      DELETE_BUDGET_TITLE: 'Delete budget',
      DELETE_DIALOG_TITLE: 'Delete item',
      DELETE_DIALOG_TITLE_MULTIPLE: 'Delete items',
      DELETE_ITEM_DIALOG_CONFIRMATION_MESSAGE: 'Please note that this action will delete all information about the item from the current budget and this cannot be undone.',
      DELETE_ITEM_DIALOG_CONFIRMATION_MESSAGE_MULTIPLE: 'Please note that this action will delete all information about the items from the current budget and this cannot be undone.',
      DELETE_ITEM_DIALOG_CONTENT_MESSAGE: 'Are you sure you want to permanently delete this item from the system?',
      DELETE_ITEM_DIALOG_CONTENT_MESSAGE_MULTIPLE: 'Are you sure you want to permanently delete these items from the system?',
      DELETE_BUDGET_DIALOG_CONTENT_MESSAGE: 'This is the last item in this budget. Deleting it will also delete the budget.',
      DELETE_BUDGET_DIALOG_CONTENT_MESSAGE_MULTIPLE: 'These are the last items in this budget. Deleting them will also delete the budget.',
      DELETE_UNSUCCESSFUL_ERROR: 'Problem with deleting item!',
      DELETE_UNSUCCESSFUL_ERROR_MULTIPLE: 'Problem with deleting items!',
      DELETE_SUCCESS: 'Successfully deleted item!',
      DELETE_SUCCESS_MULTIPLE: 'Successfully deleted items!',
      DELETE_SUCCESS_BUDGET: 'Successfully deleted item and budget!',
      DELETE_SUCCESS_BUDGET_MULTIPLE: 'Successfully deleted items and budget!',
      CREATE_NEW_ITEM_DIALOG_TITLE: 'Create new item',
      CREATE_SUCCESS: 'Successfully created new item!',
      CREATE_ERROR: 'Problem with creating new item!',
      CREATE_ERROR_DUPLICATE_INLINE_MESSAGE: 'Duplicate item. Please adjust the values',
      COMBINE_ITEM_DIALOG_TITLE: 'Combine item',
      COMBINE_ITEM_ITEMS_TO_COMBINE: (OPTS)=> `Items to be combined: (${OPTS})`,
      COMBINE_ITEM_DIALOG_MESSAGE: 'Please select main item id to keep after consolidation:',
      COMBINE_SUCCESS: 'Combined items successfully.',
      COMBINE_ERROR: 'Error combining items',
      FETCH_DATA_ERROR: 'Problem with fetching items data ',
    },
    CONFIGURE_INSIGHTS: {
      MAIN_TITLE: APP_ICONS.CONFIGURE_INSIGHTS,
      TITLE: 'Insight sets',
      PREPARE_ERROR: 'There was an error trying to fetch all insight sets!',
      CREATE_INSIGHT_SET_BUTTON: 'Create insight set',
      CREATE_SET_SUCCESS: 'Insight set created successfully',
      CREATE_SET_FAIL: 'There was an error trying to create the insight set',
      UPDATE_SET_SUCCESS: 'Insight set updated successfully',
      UPDATE_SET_FAIL: 'There was an error trying to update the insight set',
      NO_INSIGHTS_PAGE_TITLE: 'Click on an item in the menu to configure insights',
      DELETE_DIALOG: {
        TITLE: 'Delete insight set',
        WARNING: 'Are you sure you want to permanently delete this insight set from the system?',
        CONTENT: 'Please note that this action will delete all information about the insight set and this cannot be undone. ',
        DELETE_SUCCESS: 'Set deleted',
        DELETE_FAIL: 'Could not delete set',
      },
      SET_DETAILS_CARDS: {
        GOALS_TITLE: 'Goals',
        GOALS_EDIT_BUTTON: 'Edit goals',
        CATEGORIES_TITLE: `${interpolate(PROGRAM, ALIAS, nameConventions)} change categories`,
        CATEGORIES_EDIT_BUTTON: 'Edit category',
        QUESTIONS_TITLE: 'Author\'s notes questions',
        QUESTIONS_EDIT_BUTTON: 'Edit questions'
      },
      EDIT_DIALOGS: {
        EDIT_SET: {
          EDIT_SET_TITLE: `${EDIT} name`,
          CREATE_NEW_SET: 'Create new set',
          SET_DIALOG_CONTENT: 'The Insight Set name can be maximum 100 characters. Only the first 30 characters will be displayed in the Insight Excel Report.',
          ERROR_DUPLICATE: 'Set with the same name already exists',
          ERROR_LENGTH: 'Set name is too long',
          ERROR_EMPTY: 'Set name is required.'
        },
        EDIT_CATEGORIES: {
          TITLE: `Edit ${interpolate(PROGRAM, ALIAS, nameConventions)} change categories`,
          LABEL: `${interpolate(PROGRAM, ALIAS, nameConventions)} change`,
          ADD_BUTTON: `Add ${interpolate(PROGRAM, ALIAS, nameConventions)} change category`,
          ERROR_DUPLICATE:'Category already exists',
        },
        EDIT_GOALS: {
          TITLE: 'Edit goals',
          QUANTITATIVE_SUBTITLE: 'Quantitative goals:',
          QUALITATIVE_SUBTITLE: 'Qualitative goals:',
          LABEL: 'Goal',
          SELECT_LABEL: 'Select',
          NUMERIC_LABEL: 'Number',
          ERROR_DUPLICATE_DESCRIPTION:'Description already exists',
          ERROR_DUPLICATE_QUANTITY:'Quantity already exists',
          ERROR_EMPTY: REQUIRED_FIELD_TEXT,
          ERROR_NO_QUANTITY: 'Must be more than 0',
          MAXIMUM_GOALS_REACHED: 'More than 3 goals are not allowed.'
        },
        EDIT_BOX_CHOICES: {
          ERROR_DUPLICATE_TYPE:'Box choice type already exists',
          ERROR_DUPLICATE_BOX_CHOICE: 'Box choice already exists',
        },
        EDIT_QUESTIONS: {
          TITLE: 'Edit questions',
          LABEL: 'Question',
          ERROR_DUPLICATE:'Question already exists',
          REQUIRED_SWITCH: 'Required',
          ERROR_REQUIRED_FIELD: 'Question is required'
        },
      
      }
    },
    PROGRAM_INSIGHTS: {
      EMPTY_STATE_TITLE: 'No insight set yet',
      EMPTY_STATE_MESSAGE: `Get started by creating your first insight set to collect and manage insights for your ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}.`,
      EMPTY_STATE_BUTTON: 'Create insight set',
      MAIN_TITLE: `${PROGRAM} insights`,
      DETAILS_TITLE: 'Insight information',
      CREATE_TITLE: `${interpolate(PROGRAM, ALIAS, nameConventions)} insight creation`,
      VOTE_DIALOG_TITLE: 'Vote on insight',
      COMMENTS_DIALOG_TITLE: 'Edit comment',
      QUESTIONS_DIALOG_TITLE: 'Edit answers',
      HOW_TO_VOTE: 'How do you want to vote the following insight:',
      DELETE_DIALOG_TITLE: 'Delete insight',
      DELETE_ARE_YOU_SURE: 'Are you sure you want to permanently delete this insight from the system?',
      DELETE_DIALOG_CONTENT: 'Please note that this action will delete all information about the insight from this organization and this cannot be undone.',
      ERROR_DELETING_INSIGHT: 'An error occurred while deleting the insight. Something went wrong. Please try again.',
      DEPARTMENT_USERS_CAN_DELETE_ONLY_OWN_INSIGHTS: 'Department users can delete only their own insights.',
      SUCCESSFULLY_DELETED_INSIGHT: 'Successfully deleted insight!',
      ERROR_FETCHING_SETS: 'An error occurred while fetching insight sets. Something went wrong. Please try again.',
      ERROR_FETCHING_INSIGHTS: 'An error occurred while fetching insights. Something went wrong. Please try again.',
      ERROR_SAVING_VOTE: 'An error occurred while saving your vote. Something went wrong. Please try again.',
      SUCCESSFULLY_SAVED_VOTE: 'Successfully saved vote!',
      ERROR_REMOVING_VOTE: 'An error occurred while removing your vote. Something went wrong. Please try again.',
      SUCCESSFULLY_REMOVED_VOTE: 'Successfully removed vote!',
      ERROR_SAVING_COMMENT: 'An error occurred while saving your comment. Something went wrong. Please try again.',
      SUCCESSFULLY_SAVED_COMMENT: 'Successfully saved comment!',
      ERROR_FETCHING_PROGRAM_INFO: `An error occurred while fetching ${interpolate(PROGRAM, ALIAS, nameConventions)} information. Something went wrong. Please try again.`,
      ERROR_SAVING_QUESTIONS: 'An error occurred while saving your answers. Something went wrong. Please try again.',
      SUCCESSFULLY_SAVED_QUESTIONS: 'Successfully saved answers!',
      ERROR_EDITING_INSIGHT: 'An error occurred while editing the insight. Something went wrong. Please try again.',
      DEPARTMENT_USERS_CAN_EDIT_ONLY_OWN_INSIGHTS: 'Department users can edit only their own insights.',
      SUCCESSFULLY_EDITED_INSIGHT: 'Successfully edited insight!',
      ERROR_FETCHING_PROGRAMS: `An error occurred while fetching ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}. Something went wrong. Please try again.`,
      SUCCESSFULLY_CREATED_INSIGHT: 'Successfully created insight!',
      NO_INSIGHTS_FOUND: 'No insights found.',
      NO_INSIGHTS_GENERATED: 'No insights were generated. Please try again.',
      NO_VOTES_FOUND: 'No votes found.',
      NO_COMMENTS_FOUND: 'No comments found.',
      NO_QUESTIONS_FOUND: 'No questions found.',
      FILL_REQUIRED_FIELDS_BEFORE_SAVING: 'Please fill in all required fields before saving.',
      SUCCESS_DIALOG_TITLE: 'You have successfully created an insight!',
      SUCCESS_DIALOG_MESSAGE: 'Would you like to view your insight?',
      PREDICTOR_PAGE_TITLE: (OPTS) => `${OPTS} insights`,
      PREDICTOR_IMPACT_MODAL_TITLE: 'Generate AI impact insight',
      PREDICTOR_IMPACT_EXPLANATION_ONE: `Discover strategies to maximize your ${interpolate(PROGRAM, ALIAS, nameConventions)}'s impact on specific outcomes, KPIs, or metrics you care about most.`,
      PREDICTOR_IMPACT_EXPLANATION_TWO: 'Describe your priority outcome, key performance indicator, and/or metric you\'d like to impact. Be as specific as possible. The more detail you provide, the more targeted the insights will be.',
      PREDICTOR_OUTCOME_LABEL: 'Priority outcome, key performance indicator, and/or metric',
      PREDICTOR_PLAN_SECTIONS: {
        // Case-study UI is intentionally disabled for now.
        // HEADING_SECTION: {
        //   CASE_STUDY: 'Case study: ',
        // },
        MIDDLE_SECTION: {
          ACTUAL: 'What they actually did',
          IMPLEMENTATION: 'How they implemented it',
          REPLICATION: 'Your replication steps',
          TIMELINE: 'Implementation timeline'
        },
        BOTTOM_SECTION:{
          METRICS: 'Success metrics',
          CHALLENGES:'Potential challenges',
          RESOURCES: 'Resources needed'
        }
      }
    },
    SESSION_TIMEOUT: {
      TITLE: 'Sorry we dozed off! You were away so we logged you out.',
      MESSAGE: 'Sign in again to get back to what you were doing.',
    },
    MANUAL_LOGOUT: {
      TITLE: 'Looks like you\'re calling it a day.',
      MESSAGE: 'You\'ve successfully signed out. See you soon!'
    },
    SESSION_ENDED:{
      TITLE: 'You have been signed out.',
      MESSAGE: 'You can get in by clicking on the button.'
    },
    SET_ACTIVE_COST_CENTER_ERROR: `An error occurred while trying to set users active ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}. Something went wrong. Please try again.`,
    MANAGE_DATA:{
      MAIN_TITLE: APP_ICONS.MANAGE_DATA,
      FETCH_ERROR: 'Failed to fetch data',
      IMAGE_MESSAGE: 'Click on the item in the menu to manage budget data',
      USER_GROUPS: {
        TITLE: interpolate(USER_GROUP, ALIAS, nameConventions),
        TITLE_PLURAL: interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions),
        HEADING: `${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)} to be combined:`,
        SELECT_MAIN_USER_GROUP: `Please select main ${interpolate(USER_GROUP, ALIAS, nameConventions)} to keep after consolidation:`,
        EDIT_USER_GROUP_NAME: `You can edit the name of the new ${interpolate(USER_GROUP, ALIAS, nameConventions)}`,
        SAVE_SUCCESS: `${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)} saved successfully`,
        SAVE_ERROR: `Failed to save ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}.`,
        COMBINE_TITLE: `Combine ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}`,
        COMBINE_SUCCESS: `Your ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)} are successfully combined.`,
        COMBINE_ERROR: `Error combining ${interpolate(USER_GROUP, PLURAL_FORM_ALIAS, nameConventions)}. Please try again.`,
      },
      FUNDS: {
        TITLE: 'Fund',
        TITLE_PLURAL: 'Funds',
        SAVE_SUCCESS: 'Funds saved successfully',
        SAVE_ERROR: 'Failed to save funds.'
      },
      ORGANIZATION_LEVELS: {
        TITLE_PLURAL: 'Organizational levels',
        SAVE_SUCCESS: 'Organization levels saved successfully',
        SAVE_ERROR: 'Failed to save organization levels.'
      },
      OBJECT_CATEGORIES: {
        TITLE_PLURAL: 'Category levels',
        SAVE_SUCCESS: 'Object categories saved successfully',
        SAVE_ERROR: 'Failed to save object categories.'
      }
    },
    USER_ACTIVITY: {
      MAIN_TITLE: APP_ICONS.USER_ACTIVITY,
      DIALOG_TITLE: 'User activity information',
      EVENTS_FETCH_ERROR: 'There was an error fetching user activity events. Please try again later.',
      AVERAGE_TIME_FETCH_ERROR: 'There was an error fetching average session time. Please try again later.',
      USER_DETAILS_FETCH_ERROR: 'There was an error fetching user details. Please try again later.',
      AVERAGE_TIME_SPENT_MESSAGE: 'Average time spent in the system by all users.',
    },
    NO_BUDGET_EMPTY_STATE: {
      BUTTON: {
        NO_BUDGET: 'Upload',
        NO_ORG_HIERARCHY: 'Create hierarchy'
      },
      TITLE: {
        NO_BUDGET: 'No budgets yet',
        NO_ORG_HIERARCHY: 'Set up your organizational hierarchy'
      },
      MESSAGE: {
        NO_BUDGET: 'Upload your first budget to begin the PBB process.',
        NO_ORG_HIERARCHY: `The organizational hierarchy is required to create and manage ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}. Set it up to continue.`
      }

    },

    NO_PROGRAMS_EMPTY_STATE: {
      NO_PROGRAMS_FOR_BUDGET_MESSAGE: `No ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)} are added for this budget.`,
      ADD_PROGRAMS: `Add ${interpolate(PROGRAM, PLURAL_FORM_ALIAS, nameConventions)}`,
    },
    NO_RESULTS_EMPTY_STATE: {
      NO_RESULTS_FOR_BUDGET_MESSAGE: `No ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)} found.`,
      BUTTON_TEXT: `Create ${interpolate(PRIORITY, PLURAL_FORM_ALIAS, nameConventions)}`
    },
    WAIT_FOR_ADMIN_OR_SUPER_USER: 'Wait for a super user or an admin to set up the organizational hierarchy',
    SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.',
    UNSAVED_CHANGES_DIALOG_MESSAGE : 'Your changes have not been saved, what would you like to do?',
    UNSAVED_CHANGES_DIALOG_TITLE : 'Unsaved changes', 
    UNSAVED_CHANGES_DIALOG_STILL_DUPLICATES: 'You still have errors. Please ensure all errors are resolved. This action can not be performed.',
    SAVE_CHANGES_RESTRICTED_BECAUSE_USER_IS_VIEW_ONLY: 'You have read-only access and cannot save changes. Please contact your admin for changes.',
    MISSING_FILTER_TYPE: (OPTS) => `Missing filter type: ${OPTS}`,
    LOGIN_REQUIRED: 'You must be logged in to access this page.',
    ACCESS_DENIED: 'You do not have access to this page.',
    SESSION_EXPIRED: 'Your session has expired. Please log in.',
    SESSION_TIMEOUT_DIALOG_TITLE: 'Session timeout',
    SESSION_EXPIRING_PROMPT:(OPTS)=> `Looks like you've been inactive for a while. You'll be automatically signed out in ${formatTime(OPTS)} unless you hit Continue.`,
    ALREADY_LOGGED_IN: 'You are already logged in.',
    NO_ACTIVE_BUDGET: 'No budget available. Please upload a budget.',
    VIEW_ONLY_MODE: 'Read-only access. Contact your admin for changes.',
    FILE_DOWNLOAD_IN_PROGRESS_MESSAGE: 'Processing request...',
    NO_INSIGHT_SET: 'No insight set',
    RECOMMENDATION_SUCCESS: 'Scoring recommendations have been successfully generated.',
    VALID_ACTION_COLUMN_ERROR: (OPTS)=>`Please specify a valid action: ${OPTS.join(' ,')} or leave blank to ignore.`,
    ERROR_PAGES: {
      PAGE_NOT_FOUND_TITLE: 'Nothing but tumbleweeds here...',
      PAGE_NOT_FOUND_MESSAGE: 'Even our best explorer couldn\'t find the page you\'re looking for. It might have been removed or you may have mistyped the URL.',
      PAGE_NOT_FOUND_GO_BACK: 'Go back',
      PAGE_NOT_FOUND_REFRESH: 'Refresh',
      PAGE_OFFLINE_TITLE: 'Looks like you\'re off the grid.',
      PAGE_OFFLINE_MESSAGE: 'Check your internet connection and refresh the page.',
      PAGE_INTERNAL_ERROR_TITLE: 'Oops, something went wrong!',
      PAGE_INTERNAL_ERROR_MESSAGE: 'An internal error occurred. We suggest you pull over and contact your system administrator.',
      PAGE_INTERNAL_ERROR_CONTACT_ADMIN: 'Contact system admin',
      PAGE_COMING_SOON_TITLE: 'New things coming soon!',
      PAGE_COMING_SOON_MESSAGE: 'We\'re working on building something great and we can\'t wait to share it with you.',
      PAGE_OUT_OF_DATE_BROWSER_TITLE: 'Vintage is cool, but it\'s time for an upgrade.',
      PAGE_OUT_OF_DATE_BROWSER_MESSAGE: 'Install the newest version of your browser to access this app.',
      PAGE_OUT_OF_DATE_BROWSER_UPDATE_BUTTON: 'Update Browser',
      PAGE_UNSUPPORTED_BROWSER_TITLE: 'Uh oh, your browser is not supported by this application.',
      PAGE_UNSUPPORTED_BROWSER_MESSAGE: 'Hang in there. Switching to the latest version of either Google Chrome, Edge, Firefox or Safari should do the trick!',
      PAGE_UNAUTHORIZED_ACCESS_TITLE: 'Sorry, we can\'t let you in here.',
      PAGE_UNAUTHORIZED_ACCESS_MESSAGE: 'You don\'t have the security clearance to view this page. Contact your system administrator for access.',
      PAGE_FORBIDDEN_ACCESS_TITLE: 'Hey, you\'re not supposed to be in here!',
      PAGE_FORBIDDEN_ACCESS_MESSAGE: 'You don\'t have the right permission level to view this page. Contact your system administrator for access.',
      EMAIL_MISMATCH: 'Mismatched emails',
      EMAIL_MISMATCH_MESSAGE: 'The email provided by SSO does not match the email you entered. Sign out of SSO, then try again with the correct email.',
      TRY_AGAIN: 'Try again',
    }
  };
  
  return messages;
};

const getDeleteDialogConfirmation = (opts) => {
  return (
    <div>
      <p className="noMargin">
        <span>{'Organization ID:'}</span>
        <span style={{ fontWeight: 700, marginLeft: '8px' }}>{opts[0].id}</span>
      </p>
      <p className="noMargin">
        <span>{'Organization name:'}</span>
        <span style={{ fontWeight: 700, marginLeft: '8px' }}>{opts[0].name}</span>
      </p>
      <p className="noMargin">
        <span>{'Organization type:'}</span>
        <span style={{ fontWeight: 700, marginLeft: '8px' }}>{opts[0].organizationType}</span>
      </p>
    </div>
  );
};

const getLineItemsDialogTitle = (opts) => {
  switch (opts) {
    case REQUEST_TYPES_DB.INCREASE_ITEM_COST:
      return REQUEST_TYPES.INCREASE_ITEM_COST;
    case REQUEST_TYPES_DB.DECREASE_ITEM_COST:
      return REQUEST_TYPES.DECREASE_ITEM_COST;
    case REQUEST_TYPES_DB.CREATE_NEW_ITEM_FROM_EXISTING:
      return REQUEST_TYPES.CREATE_NEW_ITEM_FROM_EXISTING;
  }
};

function formatTime(seconds) {
  if (seconds > 59) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    const minuteLabel = minutes === 1 ? 'minute' : 'minutes';
    const secondLabel = remainingSeconds === 1 ? 'second' : 'seconds';

    return ` ${minutes} ${minuteLabel} and ${remainingSeconds} ${secondLabel}`;
  } else {
    const secondLabel = seconds === 1 ? 'second' : 'seconds';
    return `${seconds} ${secondLabel}`;
  }
};
